/**
 * Toggle - Switch toggle with label
 *
 * Use with React Hook Form (requires Controller for boolean values):
 * <Controller
 *   name="notifications"
 *   control={control}
 *   render={({ field }) => (
 *     <Toggle
 *       label="Enable notifications"
 *       checked={field.value}
 *       onChange={field.onChange}
 *     />
 *   )}
 * />
 *
 * Or standalone:
 * <Toggle label="Dark mode" checked={isDark} onChange={setIsDark} />
 */

import { forwardRef } from 'react';

import { cn } from '../utils';

export interface ToggleProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'type' | 'onChange' | 'size'
> {
  /** Toggle label */
  label: string;
  /** Optional description shown below the label */
  description?: string;
  /** Whether toggle is on */
  checked?: boolean;
  /** Change handler - receives boolean value */
  onChange?: (checked: boolean) => void;
  /** Size variant */
  size?: 'sm' | 'md';
  /** Layout variant - switch-left places switch before label, switch-right places label before switch */
  layout?: 'switch-left' | 'switch-right';
}

export const Toggle = forwardRef<HTMLInputElement, ToggleProps>(
  (
    {
      label,
      description,
      checked,
      onChange,
      size = 'md',
      layout = 'switch-left',
      className,
      disabled,
      ...props
    },
    ref
  ) => {
    const sizes = {
      sm: {
        track: 'h-5 w-9',
        thumb: 'h-4 w-4',
        translate: 'peer-checked:translate-x-4',
      },
      md: {
        track: 'h-6 w-11',
        thumb: 'h-5 w-5',
        translate: 'peer-checked:translate-x-5',
      },
    };

    const s = sizes[size];
    const isSwitchRight = layout === 'switch-right';

    const switchElement = (
      <div className="relative flex-shrink-0">
        <input
          ref={ref}
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange?.(e.target.checked)}
          disabled={disabled}
          className="peer sr-only"
          {...props}
        />
        <div
          className={cn(
            'rounded-full transition-colors',
            s.track,
            'peer-focus-visible:ring-2 peer-focus-visible:ring-blue-500 peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-white dark:peer-focus-visible:ring-offset-[#09090b]',
            'peer-checked:bg-blue-500 bg-gray-300 dark:bg-zinc-700',
            'peer-disabled:opacity-50'
          )}
        />
        <div
          className={cn(
            'absolute left-0.5 top-0.5 rounded-full bg-white shadow-sm transition-transform',
            s.thumb,
            s.translate
          )}
        />
      </div>
    );

    const labelElement = (
      <div className={cn('flex flex-col', isSwitchRight && 'flex-1')}>
        <span
          className={cn(
            'text-sm font-medium text-gray-700 dark:text-zinc-300',
            disabled && 'opacity-50'
          )}
        >
          {label}
        </span>
        {description && (
          <span
            className={cn('text-sm text-gray-500 dark:text-zinc-500', disabled && 'opacity-50')}
          >
            {description}
          </span>
        )}
      </div>
    );

    return (
      <div className={className}>
        <label
          className={cn(
            'flex items-center gap-3',
            isSwitchRight && 'justify-between',
            disabled ? 'cursor-not-allowed' : 'cursor-pointer'
          )}
        >
          {isSwitchRight ? (
            <>
              {labelElement}
              {switchElement}
            </>
          ) : (
            <>
              {switchElement}
              {labelElement}
            </>
          )}
        </label>
      </div>
    );
  }
);

Toggle.displayName = 'Toggle';
